import {
  BILLING_MODULE_KEYS,
  type BillingModuleKey,
} from '@bill-alps/backend/features/companies/modules';
import { Badge, Checkbox, cn } from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { formatCHF } from '@/lib/format/currency';
import { MODULE_AVAILABLE } from '@/lib/runtime/feature-flags';
import type { BillingEstimate } from '@/lib/billing/fiduciaire-billing';

/** The modules a trustee can bill: every product module the deploy ships. */
export const PICKABLE_MODULES: readonly BillingModuleKey[] =
  BILLING_MODULE_KEYS.filter((module) => MODULE_AVAILABLE[module]);

interface FiduciaireModulePickerProps {
  value: readonly BillingModuleKey[];
  onChange: (next: BillingModuleKey[]) => void;
  disabled?: boolean;
  /** Unique per rendered picker — the checkbox ids derive from it. */
  idPrefix: string;
}

/**
 * The billed-module checkboxes shared by the add-mandate dialog and the payer
 * dialog. Order is canonical (`BILLING_MODULE_KEYS`), whatever the click
 * order — the backend normalises the set the same way.
 */
export function FiduciaireModulePicker({
  value,
  onChange,
  disabled,
  idPrefix,
}: FiduciaireModulePickerProps) {
  const { t } = useTranslation('fiduciaire');
  const toggle = (module: BillingModuleKey, checked: boolean) => {
    const next = new Set(value);
    if (checked) next.add(module);
    else next.delete(module);
    onChange(PICKABLE_MODULES.filter((key) => next.has(key)));
  };

  return (
    <div className="space-y-2" data-testid="fiduciaire-module-picker">
      {PICKABLE_MODULES.map((module) => {
        const id = `${idPrefix}-${module}`;
        const checked = value.includes(module);
        return (
          <label
            key={module}
            htmlFor={id}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border-2 border-border p-3 transition-colors hover:bg-accent/30',
              checked && 'bg-primary/5',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <Checkbox
              id={id}
              checked={checked}
              disabled={disabled}
              onCheckedChange={(state) => toggle(module, state === true)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">{t(`modules.${module}`)}</p>
              <p className="text-xs text-muted-foreground">
                {t(`moduleDescriptions.${module}`)}
              </p>
            </div>
          </label>
        );
      })}
    </div>
  );
}

interface BillingEstimateSummaryProps {
  estimate: BillingEstimate;
  className?: string;
}

/**
 * What the subscription bills after the change: one line per module
 * (quantity × tier rate), the new monthly total and the delta. Tiers are
 * volume tiers, so the delta can be negative when a module crosses into a
 * cheaper band — the summary says so rather than hiding it.
 */
export function BillingEstimateSummary({
  estimate,
  className,
}: BillingEstimateSummaryProps) {
  const { t } = useTranslation('fiduciaire');
  const deltaLabel =
    estimate.delta === 0
      ? t('estimate.unchanged')
      : t('estimate.delta', {
          delta: `${estimate.delta > 0 ? '+' : '−'} ${formatCHF(Math.abs(estimate.delta))}`,
        });
  return (
    <div
      data-testid="billing-estimate"
      className={cn(
        'space-y-2 rounded-md border bg-muted/30 p-3 text-sm',
        className,
      )}
    >
      {estimate.lines.length > 0 ? (
        <ul className="space-y-1">
          {estimate.lines.map((line) => (
            <li
              key={line.module}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="text-muted-foreground">
                {t(`modules.${line.module}`)}
                <span className="ml-1 tabular-nums">
                  {t('estimate.line', {
                    quantity: line.quantity,
                    rate: formatCHF(line.rate),
                  })}
                </span>
              </span>
              <span className="tabular-nums">{formatCHF(line.monthly)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">{t('estimate.nothingBilled')}</p>
      )}
      <div className="flex items-baseline justify-between gap-3 border-t pt-2">
        <span className="font-medium">{t('estimate.total')}</span>
        <span className="flex items-baseline gap-2">
          <Badge
            variant={
              estimate.delta > 0
                ? 'amber'
                : estimate.delta < 0
                  ? 'teal'
                  : 'outline'
            }
            size="sm"
          >
            {deltaLabel}
          </Badge>
          <span className="text-base font-bold tabular-nums">
            {t('estimate.perMonth', { amount: formatCHF(estimate.after) })}
          </span>
        </span>
      </div>
    </div>
  );
}
